import { useEffect, useState, useLayoutEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { BOOK_CALL_URL } from '../config/cta'
import { FaExternalLinkAlt, FaCode, FaRocket } from 'react-icons/fa'
import ContentRenderer from '../components/content/ContentRenderer/ContentRenderer'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

interface ProjectCardProps {
  project: any
  index: number
}

function ProjectCard({ project }: ProjectCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="group relative flex flex-row bg-white rounded-xl overflow-hidden border border-gray-200/60 hover:border-gray-300 transition-all duration-300 hover:shadow-lg">
      {project.image?.url && (
        <div className="relative flex-shrink-0 w-48 md:w-56 lg:w-64 py-8 px-6 bg-gradient-to-br from-gray-50 to-white flex items-center justify-center border-r border-gray-100">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <img
            src={
              project.image.url.startsWith('http')
                ? project.image.url
                : `https://assets.gazar.dev/${project.image.url.split('/').pop()}`
            }
            alt={project.name || project.title || 'Project'}
            className="w-full h-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className="flex-1 p-6 flex flex-col min-w-0">
        <h3 className="font-heading text-lg md:text-xl font-bold text-gray-900 mb-2 tracking-tight">
          {project.name || project.title}
        </h3>
        {project.role && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-4 rounded-md bg-gray-100 text-gray-700 text-xs font-heading uppercase tracking-wide w-fit">
            <FaCode className="text-[10px]" />
            {project.role}
          </div>
        )}
        {project.content_md && (
          <div className="flex-1 relative mb-4">
            <div
              className={`font-body text-sm md:text-base leading-relaxed text-gray-700 transition-all duration-300 ${
                !isExpanded ? 'line-clamp-3' : ''
              }`}
            >
              <ContentRenderer content_md={project.content_md} maxSize="full" />
            </div>
            {!isExpanded && (
              <button
                onClick={() => setIsExpanded(true)}
                className="mt-2 text-xs font-heading font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 transition-colors"
              >
                Read More
              </button>
            )}
            {isExpanded && (
              <button
                onClick={() => setIsExpanded(false)}
                className="mt-2 text-xs font-heading font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 transition-colors"
              >
                Show Less
              </button>
            )}
          </div>
        )}
        {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center gap-2 text-sm font-heading font-semibold text-gray-900 hover:text-gray-700 transition-colors group/link"
          >
            <span>View Project</span>
            <FaExternalLinkAlt className="text-[10px] transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
          </a>
        )}
      </div>
    </div>
  )
}

export default function ProjectsPage({ items: propItems = [] }: { items?: any[] }) {
  const projects = useMemo(() => {
    const items = propItems
    return items.map((item: any) => ({
      ...item,
      createdAt: item.createdAt || item.created_at,
      updatedAt: item.updatedAt || item.updated_at,
      publishedAt: item.publishedAt || item.published_at,
      content_md: item.content_md || '',
      name: item.name || item.title,
    }))
  }, [])

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const totalCount = projects.length

  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">

      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="projects-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
              <FaCode className="text-base" aria-hidden="true" />
              Things I've built
            </span>
            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
              Projects & <InlineHighlight color="red">products</InlineHighlight>
            </h1>
            <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-12 tracking-[-0.003em]">
              Production systems, open-source tools, and side projects. Each one taught me something different about building software that works.
            </p>
            {totalCount > 0 && (
              <div className="flex flex-wrap gap-8">
                <div className="flex flex-col">
                  <div className="mb-1 font-heading text-4xl font-bold text-gray-50">{totalCount}+</div>
                  <div className="font-body text-sm text-gray-50/80">Projects</div>
                </div>
                <div className="flex flex-col">
                  <div className="mb-1 font-heading text-4xl font-bold text-gray-50">15+</div>
                  <div className="font-body text-sm text-gray-50/80">Years Experience</div>
                </div>
              </div>
            )}
          </div>
          {/* Every project row has image: null, so there are no real shots to
              show here — generated art rather than an empty column. */}
          <HeroArtGrid images={HERO_ART.projects} />
          </div>
        </ContentColumn>
      </section>

      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/60 sm:text-sm">
              <li>
                <Link to="/" className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none">
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Projects
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <ContentColumn anchor="projects-list" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {projects.length === 0 ? (
            <div className="py-20 text-center">
              <FaRocket className="mx-auto mb-4 text-6xl text-gray-300" />
              <p className="font-body text-lg text-gray-600">No projects available at this time.</p>
            </div>
          ) : (
            <>
              <div className="mb-12">
                <div className="mb-4 text-center">
                  <h2 className="mb-3 font-heading text-3xl font-bold text-gray-900 md:text-4xl">
                    All <InlineHighlight color="navy">projects</InlineHighlight>
                  </h2>
                  <p className="mx-auto max-w-2xl font-body text-base text-ink-soft">
                    Every project I've shipped, contributed to, or built from scratch
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-6">
                {projects.map((project: any, index: number) => (
                  <ProjectCard key={project.id} project={project} index={index} />
                ))}
              </div>
            </>
          )}
        </ContentColumn>
      </section>

      <section className="border-t border-gray-900/10 bg-brand-navy py-16 md:py-20">
        <ContentColumn anchor="projects-cta" className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 font-heading text-3xl font-bold text-white md:text-4xl">
            Got something to <InlineHighlight color="red">build</InlineHighlight>?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-[1.7] text-white/85">
            I'm always interested in hard problems. If you need technical leadership or want to collaborate on something, let's talk.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to={BOOK_CALL_URL}
              {...trackAttrs(CTA.bookCall, 'projects_page')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-cream px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-gray-900 transition-colors hover:bg-brand-cream/95"
            >
              Schedule a Call
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-50/40 bg-transparent px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-white transition-colors hover:border-gray-50/60 hover:bg-brand-cream/10"
            >
              Learn More About Me
            </Link>
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}
